import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { answerQuality } from '../lib/scoring';
import type { Scenario, UiCopy } from '../lib/types';

interface VerdictProps {
  ui: UiCopy;
  scenario: Scenario;
  choiceId: string;
  onContinue: () => void;
}

/**
 * The reasoning the scenario pack already carries: what the chosen answer
 * costs, what the item is testing, and the move a person would make. It stands
 * where the choices were, so the whole exchange stays on one phone screen.
 */
export function Verdict({ ui, scenario, choiceId, onContinue }: VerdictProps) {
  const choice = scenario.choices.find((entry) => entry.id === choiceId);
  const continueRef = useRef<HTMLButtonElement | null>(null);
  const quality = answerQuality(scenario, choiceId);

  useEffect(() => {
    continueRef.current?.focus({ preventScroll: true });
  }, [scenario.id]);

  if (!choice) return null;

  return (
    <motion.div
      className="verdict"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
    >
      <p className={`verdict-chip is-${quality}`} role="status">{ui.feedback[quality]}</p>

      <p className="verdict-choice">
        <span>{ui.results.yourAnswer}</span>
        {choice.text}
      </p>
      <p className="verdict-effect">{choice.effect}</p>

      <dl className="verdict-lines">
        <div>
          <dt>{ui.feedback.principle}</dt>
          <dd>{scenario.insight}</dd>
        </div>
        <div>
          <dt>{ui.feedback.check}</dt>
          <dd>{scenario.check}</dd>
        </div>
      </dl>

      <button type="button" className="action-primary" ref={continueRef} onClick={onContinue}>
        {ui.assessment.continue}
        <span aria-hidden="true">→</span>
      </button>
    </motion.div>
  );
}
